import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_5_BATERIA_SOLAR: ProgrammaticLandingPageConfig = {
  slug: 'bateria-solar-5-kwh-precio',
  lang: 'es',
  topicId: 'kwh-speicher-5',
  title: 'Batería solar de 5 kWh: precio y valores orientativos 2026',
  metaDescription:
    '¿Cuánto cuesta una batería solar de 5 kWh? Para quién es adecuada, valores orientativos de ' +
    'autoconsumo y comparación gratuita de presupuestos para instalación + batería.',
  keywords: [
    'batería solar 5 kWh precio',
    'acumulador solar 5 kWh coste',
    'batería fotovoltaica 5 kWh',
    'cuánto cuesta una batería de 5 kWh',
  ],
  eyebrow: 'Tamaño de batería: 5 kWh',
  h1: 'Batería solar de 5 kWh: precio y valores orientativos',
  intro:
    'Una batería de 5 kWh es nuestro tamaño de acumulador indexado más pequeño - un complemento ' +
    `habitual para hogares más pequeños con una instalación de referencia de ${STORAGE_REFERENCE_KWP} ` +
    'kWp. Aquí encontrará valores orientativos sobre el tamaño de hogar adecuado, el autoconsumo ' +
    'con batería y justo debajo una calculadora con el precio medio ya calculado para esta ' +
    'combinación a partir de presupuestos reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 5 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una batería de 5 kWh?' },
    {
      type: 'paragraph',
      text:
        'Valor orientativo: 5 kWh suele encajar con hogares de 1-2 personas con consumo diurno ' +
        'bajo, sin coche eléctrico ni bomba de calor. Con mayor consumo por la tarde/noche, bomba ' +
        'de calor o coche eléctrico, conviene valorar los tamaños de batería más grandes.',
    },
    { type: 'heading', text: 'Autoconsumo con batería solar' },
    {
      type: 'paragraph',
      text:
        `Sin batería, el autoconsumo de una instalación de ${STORAGE_REFERENCE_KWP} kWp ronda ` +
        `normalmente el ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Con una batería solar, este ` +
        `porcentaje sube en el modelo de cálculo unos ${SELF_CONSUMPTION_BATTERY_BONUS * 100} ` +
        `puntos hasta rondar el ${SELF_CONSUMPTION_WITH_BATTERY * 100} % - con bomba de calor o ` +
        'coche eléctrico en el hogar, el porcentaje puede seguir subiendo hasta un límite del ' +
        '85 %. Un cálculo exacto según su consumo lo ofrece la calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Suministro de emergencia' },
    {
      type: 'paragraph',
      text:
        'Muchos sistemas de batería ofrecen opcionalmente una función de suministro de emergencia ' +
        'mediante una caja de respaldo adicional - con 5 kWh, la capacidad suele alcanzar para ' +
        'algunos consumidores centrales (frigorífico, router, iluminación), no para todo el hogar. ' +
        'Si un presupuesto incluye o no esta función depende de cada instalador - conviene ' +
        'preguntarlo directamente.',
    },
    { type: 'heading', text: 'Coste de la batería y ayudas' },
    {
      type: 'paragraph',
      text:
        'El coste de las baterías varía mucho según el fabricante y la complejidad de la ' +
        `instalación - la investigación de mercado de 2026 muestra un rango de entre ` +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.min} y ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('es-ES')} ` +
        '€ por kWh. En Alemania existen ayudas específicas para baterías según la región (p. ej. ' +
        'el programa del estado federado de Berlín, con subvención para baterías); en Austria, el ' +
        'sistema EAG añade 150 €/kWh para el acumulador - un cálculo exacto para su combinación lo ' +
        'ofrece la calculadora de abajo.',
    },
  ],
  faq: [
    {
      q: '¿Merece la pena una batería de 5 kWh?',
      a: 'Para hogares pequeños con consumo bajo por la tarde/noche, una batería de 5 kWh puede aumentar notablemente el autoconsumo. Con mayor consumo (bomba de calor, coche eléctrico), un tamaño mayor suele ser más rentable.',
    },
    {
      q: '¿5 kWh es suficiente para suministro de emergencia en toda la vivienda?',
      a: 'Normalmente no - 5 kWh se adapta mejor a algunos consumidores centrales que a todo el suministro de la vivienda durante un corte de luz. Para un suministro de emergencia más amplio son habituales tamaños de batería mayores.',
    },
    {
      q: '¿Con qué tamaño de instalación se combina normalmente una batería de 5 kWh?',
      a: `Aquí se toma como referencia una instalación de ${STORAGE_REFERENCE_KWP} kWp, por ser el tamaño más elegido para viviendas unifamiliares. Con instalaciones más pequeñas, 5 kWh también suele encajar bien - la calculadora ofrece una recomendación exacta para su tejado.`,
    },
    {
      q: '¿Es suficiente 5 kWh para ser autosuficiente durante la noche?',
      a: 'Con un consumo bajo, sin bomba de calor ni carga de coche eléctrico, a menudo sí; con un consumo mayor solo parcialmente. Una estimación exacta según su consumo real la ofrece la calculadora de dimensionamiento.',
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Batería solar de 7 kWh: precio y valores orientativos', href: '/bateria-solar-7-kwh-precio' },
    { label: 'Batería solar de 10 kWh: precio y valores orientativos', href: '/bateria-solar-10-kwh-precio' },
    { label: 'Instalación fotovoltaica de 10 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-10-kwp-precio' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
  ],
}
